<script lang="ts">
	// Services
	import { handleRegister } from 'src/services/http';
	import { format } from 'src/services/i18n';
	import { createEventDispatcher } from 'svelte';
	// Helpers
	import { time_zones, current_time_zone } from 'src/helpers/date_helper';
	import { normalizeEmail } from 'src/helpers/email_helpers';
	import {
		DEFAULT_SIGNUP_PASSWORD_INPUT_PROPS,
		getSignUpRegisterAlert,
		showSignUpSuccessToast,
		validateSignUpConsents,
		validateSignUpEmail,
		validateSignUpPassword,
		validateSignUpTimezone,
	} from 'src/helpers/signup_helpers';
	import { capFirstChar, transformedTimeZones } from 'src/helpers/utilities';
	// Components
	import PasswordUtilityV2 from 'src/components/PasswordUtilityV2.svelte';
	import SignUpConsentCheckboxes from 'src/components/Profiles/signup/SignUpConsentCheckboxes.svelte';
	import SignUpLoginPrompt from 'src/components/Profiles/signup/SignUpLoginPrompt.svelte';
	import LangDropdownV2 from '../HelperComponents/LangDropdownV2.svelte';
	// Design system components
	import { Text, ButtonV2, InputV3, AlertV2, SelectV2 } from '@Shelly/shelly-design-system';
	// Types
	import type { InputV3PropsTypes, SelectV2OptionType } from '@Shelly/shelly-design-system';
	import type { LangaugeDropdownItem } from 'src/types/interfaces';
	import { user_settings } from 'src/stores/account/user';

	export let showLangDropdown = false;
	export let idPrefix = 'signup';
	export let normalizeEmailOnSubmit = true;
	export let showDropdownMenu = false;
	export let lang: string | undefined;
	export let dropdownItemsData: LangaugeDropdownItem[] | undefined;

	const dispatch = createEventDispatcher<{
		registered: { email: string; password: string };
		close: void;
		'dropdown-item-click': Partial<LangaugeDropdownItem>;
	}>();

	let timeZones = transformedTimeZones(time_zones);
	let timeZone: SelectV2OptionType = current_time_zone();

	let emailValue = '';
	let newsletterCheck = false;
	let termsCheck = false;
	let privacyPolicyCheck = false;

	let emailError: string | undefined;
	let passError: string | undefined;
	let timezoneError: string | undefined;
	let registerLoading = false;

	let inputProps: {
		size: InputV3PropsTypes['size'];
		icon: InputV3PropsTypes['icon'];
		value: InputV3PropsTypes['value'];
		label: InputV3PropsTypes['label'];
		type: InputV3PropsTypes['type'];
		disabled: InputV3PropsTypes['disabled'];
		class: string;
		autocomplete: string;
	} = { ...DEFAULT_SIGNUP_PASSWORD_INPUT_PROPS };

	$: error = passError;
	$: inputProps.disabled = registerLoading;
	$: tz = timeZones.find((el) => el.stringToVisualise === timeZone)?.stringToSend;

	let showAlert = false;
	let alertData = {
		heading: '',
		message: '',
	};

	const title = capFirstChar($format('button_sign_up'));

	const validateValues = (): boolean => {
		timezoneError = validateSignUpTimezone(tz, $format);
		emailError = validateSignUpEmail(emailValue, $format);
		passError = validateSignUpPassword(inputProps.value, $format);

		return timezoneError !== '' || emailError !== '' || passError !== '' || registerLoading;
	};

	const handleSignUp = async (): Promise<void> => {
		if (normalizeEmailOnSubmit) {
			emailValue = normalizeEmail(emailValue);
		}

		if (validateValues()) {
			return;
		}

		if (!validateSignUpConsents(termsCheck, privacyPolicyCheck, $format)) {
			return;
		}

		clearErrors();
		registerLoading = true;

		const res = await handleRegister(emailValue, inputProps.value, tz);

		if (res?.isok) {
			showSignUpSuccessToast($format);

			dispatch('registered', {
				email: emailValue,
				password: inputProps.value,
			});
			return;
		}

		alertData = getSignUpRegisterAlert(res, $format);
		showAlert = true;
		registerLoading = false;
	};

	const clearErrors = (): void => {
		timezoneError = undefined;
		emailError = undefined;
		passError = undefined;
		showAlert = false;
	};
</script>

{#if showLangDropdown}
	<div class="d-flex justify-content-between mb-l">
		<Text element="h1" class="text-center pl-s">{title}</Text>
		<LangDropdownV2
			bind:showDropdown={showDropdownMenu}
			dropdownItems={dropdownItemsData}
			{lang}
			selectedLang={$user_settings.lang}
			on:change-lang={(e) => {
				dispatch('dropdown-item-click', e.detail);
			}}
		/>
	</div>
{:else}
	<Text element="h1" class="mb-l pl-s">{title}</Text>
{/if}

{#if showAlert}
	<AlertV2 type="danger" heading={alertData.heading} message={alertData.message} class="mb-m" />
{/if}

<InputV3
	size="l"
	class="mb-m"
	id="{idPrefix}-email-input"
	aria-label={$format('placeholder_email', { default: 'Email' })}
	disabled={registerLoading}
	bind:value={emailValue}
	label={capFirstChar($format('placeholder_email', { default: 'enter your e-mail' }))}
	icon="far fa-envelope"
	type="text"
	inputmode="email"
	error={emailError}
	autocomplete="off"
	on:focus={clearErrors}
	on:enter={handleSignUp}
/>

<PasswordUtilityV2 bind:inputProps bind:error on:focus={clearErrors} on:enter={handleSignUp} />

<SelectV2
	size="l"
	class="mb-m signup-timezone-select"
	id="{idPrefix}-timezone-select"
	aria-label={$format('label_select_timezone', { default: 'Select timezone' })}
	disabled={registerLoading}
	value={timeZone}
	isAutocomplete
	icon="far fa-globe"
	error={timezoneError}
	options={timeZones.map((zone) => zone.stringToVisualise)}
	disableOptionsPortal
	on:change={(e) => (timeZone = e.detail)}
	on:focus={clearErrors}
/>

<SignUpConsentCheckboxes
	{idPrefix}
	checkboxVariant="light"
	bind:termsCheck
	bind:privacyPolicyCheck
	bind:newsletterCheck
	disabled={registerLoading}
/>

<ButtonV2
	class="text-capitalize"
	variant="primary"
	width="full"
	disabled={!termsCheck || !privacyPolicyCheck || registerLoading}
	loading={registerLoading}
	on:click={handleSignUp}
>
	{capFirstChar($format('button_sign_up', { default: 'Sign up' }))}
</ButtonV2>

<SignUpLoginPrompt disabled={registerLoading} on:loginClick={() => dispatch('close')} />
